import { select, selectAll, pointer } from "d3-selection";
import { min, max, extent, bisector } from "d3-array";
import { line, symbol, symbolTriangle } from "d3-shape";
import { scaleLinear, scaleTime, scaleSymlog } from "d3-scale";
import { axisBottom, axisLeft } from "d3-axis";
import { format } from "d3-format";
import { timeParse, timeFormat } from "d3-time-format";
import { transition } from "d3-transition";
import { Tooltip } from "./tooltip";

// Format timeliness in seconds to "Xm YYs" format
export const formatTimeliness = seconds => {
  if (seconds === null || seconds === undefined) return "No data";
  if (seconds === 0) return "0m 00s";
  if (seconds < 60) return `0m ${Math.round(seconds)}s`;

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remainingSeconds.toString().padStart(2, "0")}s`;
};

function clampTimelinessY(y, yMin, yMax) {
  let y_in_minutes = y / 60;
  if (y_in_minutes < yMin)
    return {
      yClamped: yMin,
      outOfBounds: true,
      direction: "down",
      originalY: y
    };
  if (y_in_minutes > yMax)
    return {
      yClamped: yMax,
      outOfBounds: true,
      direction: "up",
      originalY: y
    };
  return {
    yClamped: y_in_minutes,
    outOfBounds: false,
    direction: null,
    originalY: y
  };
}
export class D3Chart {
  constructor(parentElement, data) {
    this.parentElement = parentElement;
    this.data = data.series;
    this.rangeX = data.rangeX;
    this.dateFormats = data.dateFormats;
    this.metadataYAxis = data.metadataYAxis;
    this.label = data.metadataYAxis.label;
    this.initVis();
  }

  initVis() {
    // Set margins
    this.margin = { top: 10, right: 20, bottom: 30, left: 50 };

    // Get container dimensions
    const containerChart = document.getElementById(this.parentElement);
    const positionInfo = containerChart.getBoundingClientRect();

    // Calculate dimensions
    this.width = positionInfo.width - this.margin.left - this.margin.right;
    this.height = positionInfo.height - this.margin.top - this.margin.bottom;

    // 1) Set up SVG and g elements
    this.svg = select(`#${this.parentElement}`)
      .append("svg")
      .attr("id", "svg-time-series")
      .attr("width", this.width + this.margin.left + this.margin.right)
      .attr("height", this.height + this.margin.top + this.margin.bottom);

    this.g = this.svg
      .append("g")
      .attr("transform", `translate(${this.margin.left},${this.margin.top})`);

    // Set up transition
    this.t = () => transition().duration(1000);

    // 2) Calculate axes
    const formatDate = "%Y-%m-%dT%H:%M:%SZ";
    this.parserTime = timeParse(formatDate);
    this.x = scaleTime()
      .range([0, this.width])
      .domain(extent(this.rangeX, d => this.parserTime(d)));

    // Calculate Y-axis domain
    const allValues = [];

    // Collect all y values from the data points
    for (const key in this.data) {
      this.data[key].data.forEach(d => {
        if (d.y !== null) {
          allValues.push(d.y);
        }
      });
    }

    // Determine if we should use symmetric logarithmic scale
    const useLogScale = this.metadataYAxis.useLogScale === true;
    const label = this.label
      ? this.label.toLowerCase()
      : (this.metadataYAxis.label || "").toLowerCase();
    const isTimeliness = label.includes("timeliness");
    const isAvailability = label.includes("nb. obs");
    const isQuality = label.includes("o-b");
    const isWis2Gts = label.includes("messages");
    if (isWis2Gts) {
      // Check if this is tac-to-bufr report by looking for bufr_nr_received series key
      const isBufrVsTac = this.data.hasOwnProperty("bufr_nr_received");
      const isWis2Gts = this.data.hasOwnProperty("nr_received_wis2");
      if (isBufrVsTac || isWis2Gts) {
        // Dynamic Y-axis for tac-to-bufr report

        // Calculate actual max/min from the data
        let actualMax = -Infinity;
        let actualMin = Infinity;

        Object.values(this.data).forEach(series => {
          if (series.max > actualMax) actualMax = series.max;
          if (series.min < actualMin) actualMin = series.min;
        });

        const yMin = actualMin !== Infinity ? Math.max(0, actualMin - 5) : 0;
        const yMax = actualMax !== -Infinity ? actualMax + 5 : 100;

        this.y = scaleLinear()
          .domain([yMin, yMax])
          .nice()
          .range([this.height, 0]);
        this.yAxisCall = axisLeft(this.y).tickFormat(d => d);
      } else {
        // Static 0-24 range for other WIS2/GTS reports
        const yTicks = [];
        for (let i = 0; i <= 24; i++) {
          yTicks.push(i);
        }
        const yMin = 0;
        const yMax = 24;

        this.y = scaleLinear()
          .domain([yMin, yMax])
          .nice()
          .range([this.height, 0]);
        this.yAxisCall = axisLeft(this.y)
          .tickValues(yTicks)
          .tickFormat(d => d);
      }
    } else if (isTimeliness) {
      // Timeliness: fixed SymLog or linear scale, fixed ticks
      const yTicks = [-60, -30, 0, 30, 60, 120, 360, 720];
      const yMin = -60;
      const yMax = 720;
      this.minY = yMin;
      if (useLogScale) {
        try {
          const constant = 1;
          this.y = scaleSymlog()
            .domain([yMin, yMax])
            .nice()
            .constant(constant)
            .range([this.height, 0]);
          this.yAxisCall = axisLeft(this.y)
            .tickValues(yTicks)
            .tickFormat(d => d);
        } catch (error) {
          console.error(
            "Error setting up SymLog scale, falling back to linear scale",
            error
          );
          this.y = scaleLinear()
            .domain([yMin, yMax])
            .nice()
            .range([this.height, 0]);
          this.yAxisCall = axisLeft(this.y)
            .tickValues(yTicks)
            .tickFormat(d => d);
        }
      } else {
        this.y = scaleLinear()
          .domain([yMin, yMax])
          .nice()
          .range([this.height, 0]);
        this.yAxisCall = axisLeft(this.y)
          .tickValues(yTicks)
          .tickFormat(d => d);
      }
    } else if (isAvailability) {
      // Availability: dynamic integer ticks (restore old logic)
      // Calculate min/max from all series
      const maxs = [];
      const mins = [];
      for (const key in this.data) {
        maxs.push(this.data[key].max);
        mins.push(this.data[key].min);
      }
      this.maxY = max(maxs);
      this.minY =
        this.metadataYAxis.min !== undefined && this.metadataYAxis.min !== null
          ? this.metadataYAxis.min
          : min(mins);
      this.y = scaleLinear()
        .domain([this.minY, this.maxY])
        .nice()
        .range([this.height, 0]);
      const yTicks = Math.min(10, Math.round(this.maxY - this.minY));
      this.yAxisCall = axisLeft(this.y)
        .ticks(yTicks)
        .tickFormat(format("d"));
    } else if (isQuality) {
      // Quality: dynamic float ticks (restore old logic)
      const maxs = [];
      const mins = [];
      for (const key in this.data) {
        maxs.push(this.data[key].max);
        mins.push(this.data[key].min);
      }
      this.maxY = max(maxs);
      this.minY =
        this.metadataYAxis.min !== undefined && this.metadataYAxis.min !== null
          ? this.metadataYAxis.min
          : min(mins);
      this.y = scaleLinear()
        .domain([this.minY, this.maxY])
        .nice()
        .range([this.height, 0]);
      const yTicks = Math.min(10, Math.round(this.maxY - this.minY));
      this.yAxisCall = axisLeft(this.y)
        .ticks(yTicks)
        .tickFormat(format(this.metadataYAxis.format));
    } else {
      // All other reports: dynamic domain, linear scale, default ticks
      const actualMax = max(allValues) || 0;
      const actualMin = min(allValues) || 0;
      const range = Math.abs(actualMax - actualMin);
      const padding = range * 0.1;
      let minY = actualMin - padding;
      let maxY = actualMax + padding;
      this.minY = minY;
      if (maxY === minY) {
        maxY += 1;
        minY -= 1;
      }
      this.y = scaleLinear()
        .domain([minY, maxY])
        .nice()
        .range([this.height, 0]);
      // Use default ticks for linear scale
      const rangeSize = Math.abs(maxY - minY);
      const yTicks = Math.min(10, Math.max(2, Math.round(rangeSize / 10)));
      this.yAxisCall = axisLeft(this.y)
        .ticks(yTicks)
        .tickFormat(format(this.metadataYAxis.format));
    }

    this.yAxis = this.g
      .append("g")
      .attr("transform", "translate(0, 0)")
      .call(this.yAxisCall);

    // Add Y-axis label
    this.g
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", -50)
      .attr("x", -(this.height / 2))
      .attr("dy", ".8em")
      .style("text-anchor", "middle")
      .attr("fill", "#5D6971")
      .text(this.metadataYAxis.label);

    // Add zero line if minY < 0
    if (this.metadataYAxis.y0 && this.minY < 0) {
      this.g
        .append("line")
        .classed("y-zero", true)
        .attr("x1", this.x(this.parserTime(this.rangeX[0])))
        .attr("y1", this.y(0))
        .attr(
          "x2",
          this.x(this.parserTime(this.rangeX[this.rangeX.length - 1]))
        )
        .attr("y2", this.y(0))
        .attr("stroke", "rgb(31, 51, 73, 0.5)")
        .attr("stroke-width", 0.5)
        .attr("stroke-dasharray", "4, 2");
    }

    // Create X-axis
    this.xAxisCall = axisBottom(this.x)
      .ticks(5)
      .tickFormat(timeFormat(this.dateFormats.xAxis));

    this.xAxis = this.g
      .append("g")
      .attr("transform", `translate(0,${this.height})`)
      .call(this.xAxisCall);

    this.wrangleData();
  }

  wrangleData() {
    for (const serie in this.data) {
      // Identify isolated values for lines/dashlines
      if (
        this.data[serie].representAs === "lines" ||
        this.data[serie].representAs === "dashlines"
      ) {
        this.data[serie].isolated = identifyIsolatedValues(this.data[serie]);
      }

      // Add rangeX for all series
      this.data[serie].rangeX = this.data[serie].data.map(d => d.x);
    }
    this.updateVis();
  }

  updateVis() {
    const vis = this;
    const yMin = -60;
    const yMax = 720;
    let hasOutOfBounds = false;
    // Add interactivity in the begining as it has rectangle overlay that will conflict with triangle markers
    this.addInteractivity();
    for (const serie in this.data) {
      // Draw lines
      if (
        this.data[serie].representAs === "lines" ||
        this.data[serie].representAs === "dashlines"
      ) {
        let lineGenerator = line()
          .defined(d => d.y !== null)
          .x(d => vis.x(vis.parserTime(d.x)))
          .y(d => vis.y(d.y));
        if (
          this.data[serie].data.some(
            d => d && d.hasOwnProperty("nr_negative_timeliness")
          ) ||
          this.data[serie].label.includes("timeliness")
        ) {
          lineGenerator = line()
            .defined(d => d.y !== null)
            .x(d => vis.x(vis.parserTime(d.x)))
            .y(d => vis.y(d.y / 60));
        }

        const lineType = this.data[serie].representAs;
        this.g
          .append("path")
          .attr("class", "line")
          .attr("fill", "none")
          .attr("stroke", this.data[serie].color)
          .attr("stroke-width", 1.5)
          .attr("stroke-dasharray", lineType === "dashlines" ? "2, 2" : null)
          .attr("d", lineGenerator(this.data[serie].data));

        // Add dots to timeliness lines in combined mode
        if (
          lineType === "lines" &&
          this.data[serie].data.some(
            d => d && d.hasOwnProperty("nr_negative_timeliness")
          )
        ) {
          this.g
            .selectAll(`circle.dots-${serie}`)
            .data(this.data[serie].data.filter(d => d.y !== null))
            .enter()
            .append("circle")
            .attr("class", `dots-${serie}`)
            .attr("cx", d => vis.x(vis.parserTime(d.x)))
            .attr("cy", d => vis.y(d.y / 60)) //
            .attr("r", 3)
            .attr("fill", this.data[serie].color)
            .attr("stroke", d =>
              d.nr_negative_timeliness > 0 ? "#FF1744" : "none"
            )
            .attr("stroke-width", d =>
              d.nr_negative_timeliness > 0 ? 1.5 : 0
            );
        }

        if (this.data[serie].isolated && this.data[serie].isolated.length > 0) {
          if (
            this.data.individual &&
            this.data.individual.label.includes("Timeliness")
          ) {
            this.g
              .selectAll(`circle.isolated`)
              .data(this.data[serie].isolated)
              .enter()
              .append("circle")
              .attr("cx", d => vis.x(vis.parserTime(d.x)))
              .attr("cy", d => vis.y(d.y / 60))
              .attr("r", 2)
              .attr("fill", this.data[serie].color);
          } else {
            this.g
              .selectAll(`circle.isolated`)
              .data(this.data[serie].isolated)
              .enter()
              .append("circle")
              .attr("cx", d => vis.x(vis.parserTime(d.x)))
              .attr("cy", d => vis.y(d.y))
              .attr("r", 2)
              .attr("fill", this.data[serie].color);
          }
        }

        this.g
          .selectAll(`circle.isolated`)
          .data(this.data[serie].isolated)
          .enter()
          .append("circle")
          .attr("cx", d => vis.x(vis.parserTime(d.x)))
          .attr("cy", d => vis.y(d.y))
          .attr("r", 2)
          .attr("fill", this.data[serie].color);
      }
      // Draw points
      if (this.data[serie].representAs === "points") {
        // do below only for timeliness single center
        if (
          this.data.individual &&
          this.data.individual.label.includes("Timeliness")
        ) {
          this.data[serie].data.forEach((d, i) => {
            if (d.y !== null && d.x !== null) {
              let {
                yClamped,
                outOfBounds,
                direction,
                originalY
              } = clampTimelinessY(d.y, yMin, yMax);
              const xVal = this.x(this.parserTime(d.x));
              const yVal = this.y(yClamped);
              // get the datetime
              const displayTime = d.x;
              if (outOfBounds) {
                hasOutOfBounds = true;
                // Draw triangle marker at the edge
                const triangle = this.g
                  .append("path")
                  .attr(
                    "d",
                    symbol()
                      .type(symbolTriangle)
                      .size(20)
                  )
                  .attr(
                    "transform",
                    `translate(${xVal},${yVal}) rotate(${
                      direction === "down" ? 180 : 0
                    })`
                  )
                  .attr("fill", "orange")
                  .attr("stroke", "red")
                  .attr("class", "clamped-marker")
                  .on("mouseover", event => {
                    const dataForTooltip = {};
                    dataForTooltip[serie] = {
                      label: "Timeliness in minutes",
                      color: "orange",
                      data: { y: formatTimeliness(originalY) },
                      tooltip: this.data[serie].tooltip
                    };
                    vis.tooltip.show(
                      event,
                      displayTime,
                      dataForTooltip,
                      vis.dateFormats.tooltip
                    );
                  });

                triangle.raise(); // raise triangle above other elements
                // .attr("stroke", "white");
              } else {
                // Draw normal point
                this.g
                  .append("circle")
                  .attr("cx", xVal)
                  .attr("cy", yVal)
                  .attr("r", 3)
                  .attr("fill", d =>
                    typeof vis.data[serie].color === "function"
                      ? vis.data[serie].color(d)
                      : vis.data[serie].color
                  )
                  .attr("class", "clamped-marker")
                  .on("mouseover", event => {
                    const dataForTooltip = {};
                    dataForTooltip[serie] = {
                      label: "Timeliness in minutes",
                      color: vis.data[serie].color,
                      data: { y: originalY },
                      tooltip: this.data[serie].tooltip
                    };
                    vis.tooltip.show(
                      event,
                      displayTime,
                      dataForTooltip,
                      vis.dateFormats.tooltip
                    );
                  });
              }
            }
          });
        } else {
          this.g
            .selectAll(`circle.isolated`)
            .data(this.data[serie].data.filter(d => d.y !== null))
            .enter()
            .append("circle")
            .attr("cx", d => vis.x(vis.parserTime(d.x)))
            .attr("cy", d => vis.y(d.y))
            .attr("r", 2)
            .attr("fill", d =>
              typeof vis.data[serie].color === "function"
                ? vis.data[serie].color(d)
                : vis.data[serie].color
            )
            .attr("class", "clamped-marker")
            .on("mouseover", function(event, d) {
              const dataForTooltip = {};
              dataForTooltip[serie] = {
                label: vis.data[serie].label,
                color:
                  typeof vis.data[serie].color === "function"
                    ? vis.data[serie].color(d)
                    : vis.data[serie].color,
                data: { y: formatTimeliness(d.y) },
                tooltip: vis.data[serie].tooltip
              };
              vis.tooltip.show(
                event,
                d.x,
                dataForTooltip,
                vis.dateFormats.tooltip
              );
            });
        }
      }
      // Add warning if any point is out of bounds
      if (hasOutOfBounds) {
        this.svg
          .append("text")
          .attr("x", this.width - 400)
          .attr("class", "out-of-bounds-warning")
          .attr("y", 30)
          .attr("fill", "orange")
          .attr("font-size", "12px")
          .text(
            "Timeliness values exceeding minus 60 are not drawn but marked with a triangle."
          );
      }
    }
  }

  addInteractivity() {
    const vis = this;

    // Check if rangeX is in reverse chronological order
    const isReversed =
      vis.rangeX.length > 1 &&
      new Date(vis.rangeX[0]) > new Date(vis.rangeX[1]);

    // Create appropriate bisector based on order
    const bisectDate = isReversed
      ? bisector(d => -vis.parserTime(d)).left // Negative for reverse order
      : bisector(d => vis.parserTime(d)).left;

    // Detect if this is hourly period from rangeX structure
    // Handle both forward and reverse chronological order
    let timeDiff = 0;
    if (vis.rangeX.length >= 2) {
      timeDiff = Math.abs(new Date(vis.rangeX[1]) - new Date(vis.rangeX[0]));
    }
    const isHourlyPeriod = vis.rangeX.length > 100 && timeDiff === 3600000; // 1 hour in milliseconds

    // Initialize tooltip
    vis.tooltip = new Tooltip(vis); // Pass chart props to Tooltip

    vis.g
      .append("rect")
      .attr("class", "overlay")
      .attr("fill", "none")
      .style("pointer-events", "all")
      .attr("width", vis.width)
      .attr("height", vis.height)
      .on("mousemove", mousemove);

    function mousemove(event) {
      // Remove previous highlights
      selectAll(".highlighted").remove();

      // Calculate mouse position
      const xMouse = vis.x.invert(pointer(event)[0]);

      // Find nearest time step manually for reverse chronological order
      let nearestIndex = 0;
      let minDiff = Infinity;

      for (let i = 0; i < vis.rangeX.length; i++) {
        const timeDiff = Math.abs(vis.parserTime(vis.rangeX[i]) - xMouse);
        if (timeDiff < minDiff) {
          minDiff = timeDiff;
          nearestIndex = i;
        }
      }

      const displayTime = vis.rangeX[nearestIndex];

      if (displayTime) {
        // Show vertical line
        vis.g
          .append("line")
          .classed("highlighted", true)
          .attr("x1", vis.x(vis.parserTime(displayTime)))
          .attr("y1", vis.y(vis.y.domain()[1]))
          .attr("x2", vis.x(vis.parserTime(displayTime)))
          .attr("y2", vis.y(vis.y.domain()[0]))
          .attr("stroke", "rgb(31, 51, 73, 0.5)")
          .attr("stroke-width", 1);

        const dataForTooltip = {};

        // Highlight points - check if data exists for this time in each series
        for (const serie in vis.data) {
          // Find the data point for this time in the series
          const dataPoint = vis.data[serie].data.find(d => d.x === displayTime);

          if (dataPoint && dataPoint.y !== null) {
            dataForTooltip[serie] = {
              label: vis.data[serie].label,
              color: vis.data[serie].color,
              data: dataPoint,
              tooltip: vis.data[serie].tooltip
            };

            // Check if this is timeliness data (needs conversion from seconds to minutes)
            const isTimelinessData =
              vis.data[serie].data.some(
                d => d && d.hasOwnProperty("nr_negative_timeliness")
              ) || vis.data[serie].label.includes("timeliness");

            vis.g
              .append("circle")
              .classed("highlighted", true)
              .attr("cx", vis.x(vis.parserTime(displayTime)))
              .attr(
                "cy",
                isTimelinessData ? vis.y(dataPoint.y / 60) : vis.y(dataPoint.y)
              )
              .attr("r", 3)
              .attr("stroke", "white")
              .attr("stroke-width", 2)
              .attr(
                "fill",
                typeof vis.data[serie].color === "function"
                  ? vis.data[serie].color(dataPoint, "status")
                  : vis.data[serie].color
              );
          }
        }

        // Show tooltip even if no data exists for this time point
        // This ensures hover works for all hourly ticks - ONLY for hourly periods
        if (Object.keys(dataForTooltip).length === 0 && isHourlyPeriod) {
          // Create empty tooltip data to show the time
          dataForTooltip["no_data"] = {
            label: "No data",
            color: "#ccc",
            data: { y: null },
            tooltip: "point"
          };
        }

        // Show tooltip only if we have data OR if it's hourly period
        if (Object.keys(dataForTooltip).length > 0) {
          vis.tooltip.show(
            event,
            displayTime,
            dataForTooltip,
            vis.dateFormats.tooltip
          );
        }
      }
    }
  }
}

// Helper function to identify isolated values
export const identifyIsolatedValues = serie => {
  const isolated = [];

  if (serie.data.length === 1) {
    return serie.data;
  }

  // Check first element
  const dFirst = serie.data[0];
  if (dFirst && dFirst.y !== null) {
    const next = serie.data[1];
    if (next.y === null) {
      isolated.push(dFirst);
    }
  }

  // Check middle elements
  for (let i = 1; i < serie.data.length - 1; i++) {
    const d = serie.data[i];
    if (d && d.y !== null) {
      const prev = serie.data[i - 1];
      const next = serie.data[i + 1];
      if (prev.y === null && next.y === null) {
        isolated.push(d);
      }
    }
  }

  // Check last element
  const dLast = serie.data[serie.data.length - 1];
  if (dLast && dLast.y !== null) {
    const prev = serie.data[serie.data.length - 2];
    if (prev.y === null) {
      isolated.push(dLast);
    }
  }

  return isolated;
};
